import React, { useEffect, useState, useCallback } from "react";
import { Newspaper, ExternalLink, ThumbsUp, ThumbsDown, Sparkles, RefreshCw, Clock, Send, Mail, Sunrise, Sunset, CalendarDays, TrendingUp, TrendingDown, Bell, Building2, Target, BellRing } from "lucide-react";
import { getStockNews, getMarketPulse, getMorningSummary, sendMorningToTelegram, getEveningWrap, sendEveningToTelegram, getClosingBell, sendClosingBellToTelegram, getWeekendReview, sendWeekendToTelegram, sendWeekendToEmail } from "../lib/api";
import { toast } from "sonner";

const SOURCE_COLORS = {
  "Google News": "bg-blue-500/10 border-blue-500/20 text-blue-300",
  "Finnhub": "bg-purple-500/10 border-purple-500/20 text-purple-300",
  "moneycontrol": "bg-amber-500/10 border-amber-500/20 text-amber-300",
  "mint_markets": "bg-pink-500/10 border-pink-500/20 text-pink-300",
  "et_markets": "bg-red-500/10 border-red-500/20 text-red-300",
  "finshots": "bg-emerald-500/10 border-emerald-500/20 text-emerald-300",
  "finnhub": "bg-purple-500/10 border-purple-500/20 text-purple-300",
};

const SOURCE_LABELS = {
  "moneycontrol": "Moneycontrol",
  "mint_markets": "Mint",
  "et_markets": "ET Markets",
  "finshots": "Finshots",
  "finnhub": "Finnhub",
};

function timeAgo(iso) {
  if (!iso) return "";
  const now = new Date();
  const then = new Date(iso);
  const mins = Math.floor((now - then) / 60000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d`;
  return then.toLocaleDateString();
}

function SentimentPill({ sentiment, compact = false }) {
  if (!sentiment || !sentiment.total) return null;
  const { label, positive, negative, total } = sentiment;
  const color = label === "positive"
    ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/20"
    : label === "negative"
    ? "bg-red-500/10 text-red-300 border-red-500/20"
    : "bg-white/5 text-[var(--text-muted)] border-white/10";
  const Icon = label === "positive" ? ThumbsUp : label === "negative" ? ThumbsDown : null;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] border ${color}`}>
      {Icon && <Icon size={10} />}
      {compact ? label : <>Sentiment: <b className="ml-0.5">{label}</b> · {positive}↑/{negative}↓ of {total}</>}
    </span>
  );
}

function NewsArticle({ a, compact = false }) {
  const srcColor = SOURCE_COLORS[a.source] || "bg-white/5 border-white/10 text-[var(--text-muted)]";
  const srcLabel = SOURCE_LABELS[a.source] || a.source;
  return (
    <a
      href={a.url}
      target="_blank"
      rel="noreferrer"
      className="block p-3 border border-white/10 bg-[var(--bg-surface)] hover:border-white/20 hover:bg-white/[0.02] rounded-md transition-colors group"
      data-testid={`news-${a.id}`}
    >
      <div className="flex items-start gap-3">
        {a.image && !compact && (
          <img
            src={a.image}
            alt=""
            className="w-20 h-16 object-cover rounded flex-shrink-0 border border-white/5"
            loading="lazy"
          />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-1 flex-wrap">
            <span className={`text-[9px] px-1.5 py-0.5 rounded border ${srcColor}`}>{srcLabel}</span>
            {a.published_at && (
              <span className="text-[10px] text-[var(--text-muted)] flex items-center gap-0.5">
                <Clock size={9} /> {timeAgo(a.published_at)}
              </span>
            )}
          </div>
          <div className="text-sm text-white leading-snug group-hover:text-[var(--brand)] transition-colors">
            {a.title}
          </div>
          {a.description && !compact && (
            <p className="text-[11px] text-[var(--text-muted)] mt-1 line-clamp-2">{a.description}</p>
          )}
        </div>
        <ExternalLink size={12} className="text-[var(--text-muted)] opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-1" />
      </div>
    </a>
  );
}

// ── Per-stock news panel ────────────────────────────────────────────
export function StockNewsPanel({ symbol, country = "India" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!symbol) return;
    setLoading(true);
    try {
      const { data } = await getStockNews(symbol, country, 12);
      setData(data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [symbol, country]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="bg-[var(--bg-surface)] border border-white/10 rounded-lg p-5 animate-pulse">
        <div className="h-4 w-40 bg-white/5 rounded mb-3" />
        <div className="space-y-2">
          {[...Array(3)].map((_, i) => <div key={i} className="h-16 bg-white/5 rounded" />)}
        </div>
      </div>
    );
  }
  if (!data || !data.articles?.length) return null;

  return (
    <div className="bg-[var(--bg-surface)] border border-white/10 rounded-lg p-5" data-testid="stock-news-panel">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Newspaper size={15} className="text-[var(--brand)]" />
          <h3 className="text-sm font-semibold text-white tracking-wide" style={{ fontFamily: "Chivo" }}>
            Latest News
          </h3>
          <span className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">{data.articles.length} articles</span>
        </div>
        <div className="flex items-center gap-2">
          <SentimentPill sentiment={data.sentiment} />
          <button onClick={load} className="p-1 text-[var(--text-muted)] hover:text-white" data-testid="refresh-stock-news">
            <RefreshCw size={12} />
          </button>
        </div>
      </div>
      <div className="space-y-2">
        {data.articles.map((a) => <NewsArticle key={a.id} a={a} />)}
      </div>
    </div>
  );
}

// ── Market Pulse (Dashboard) ────────────────────────────────────────
export function MarketPulsePanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("headlines");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getMarketPulse(8);
      setData(data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="bg-[var(--bg-surface)] border border-white/10 rounded-lg p-5 animate-pulse">
        <div className="h-4 w-40 bg-white/5 rounded mb-4" />
        <div className="space-y-2">
          {[...Array(4)].map((_, i) => <div key={i} className="h-14 bg-white/5 rounded" />)}
        </div>
      </div>
    );
  }
  if (!data) return null;

  const headlines = data.top_headlines || [];
  const explainers = data.explainers || [];

  return (
    <div className="space-y-4">
      <div className="bg-[var(--bg-surface)] border border-white/10 rounded-lg p-5" data-testid="market-pulse-panel">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Newspaper size={15} className="text-[var(--brand)]" />
            <h3 className="text-sm font-semibold text-white tracking-wide" style={{ fontFamily: "Chivo" }}>
              Market Pulse
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <SentimentPill sentiment={data.sentiment} />
            <button onClick={load} className="p-1 text-[var(--text-muted)] hover:text-white" data-testid="refresh-pulse">
              <RefreshCw size={12} />
            </button>
          </div>
        </div>

        <div className="flex gap-1 mb-3 text-xs">
          <TabBtn active={tab === "headlines"} onClick={() => setTab("headlines")} testid="pulse-tab-headlines">
            Headlines ({headlines.length})
          </TabBtn>
          {explainers.length > 0 && (
            <TabBtn active={tab === "explainers"} onClick={() => setTab("explainers")} testid="pulse-tab-explainers">
              <Sparkles size={11} className="inline mr-0.5" /> Finshots ({explainers.length})
            </TabBtn>
          )}
        </div>

        {tab === "headlines" && (
          <div className="space-y-2 max-h-[500px] overflow-y-auto" data-testid="pulse-headlines">
            {headlines.map((a) => <NewsArticle key={a.id} a={a} compact />)}
            {headlines.length === 0 && (
              <div className="text-center text-[var(--text-muted)] text-xs py-4">No headlines available.</div>
            )}
          </div>
        )}
        {tab === "explainers" && (
          <div className="space-y-2 max-h-[500px] overflow-y-auto" data-testid="pulse-explainers">
            {explainers.map((a) => <NewsArticle key={a.id} a={a} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function BrokerStrip({ rows, label = "today" }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div className="mt-3 pt-3 border-t border-white/5" data-testid="brief-broker-strip">
      <div className="text-[11px] uppercase tracking-wider text-[var(--text-muted)] mb-2 flex items-center gap-1.5">
        <Building2 size={12} /> Broker-wise ({label})
      </div>
      <div className="space-y-1 max-h-[150px] overflow-y-auto">
        {rows.slice(0, 8).map((b) => {
          const available = b.delta_value !== null && b.delta_value !== undefined;
          const dv = b.delta_value || 0;
          const dp = b.delta_pct || 0;
          const up = dv >= 0;
          const color = up ? "text-emerald-400" : "text-red-400";
          const name = b.account_name || b.broker || b.account_id;
          return (
            <div key={b.account_id} className="text-[13px] flex justify-between items-baseline gap-2">
              <span className="font-mono text-white truncate">{name}</span>
              {available ? <span className={`font-mono ${color} whitespace-nowrap`}>
                {up ? "+" : ""}{dv.toLocaleString("en-IN", { maximumFractionDigits: 0 })} ({up ? "+" : ""}{dp.toFixed(2)}%)
              </span> : <span className="font-mono text-amber-300 whitespace-nowrap" title={`${b.quoted_holdings || 0}/${b.eligible_holdings || 0} live quotes`}>Unavailable</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function IdeaChangesStrip({ changes }) {
  if (!changes || !changes.has_two_runs) return null;
  const newPicks = changes.new_picks || [];
  const dropped = changes.dropped_picks || [];
  const shifts = changes.score_changes || [];
  if (newPicks.length === 0 && dropped.length === 0 && shifts.length === 0) return null;
  return (
    <div className="mt-3 pt-3 border-t border-white/5" data-testid="brief-idea-changes">
      <div className="text-[11px] uppercase tracking-wider text-[var(--text-muted)] mb-2 flex items-center gap-1.5">
        <Target size={12} className="text-sky-300" /> AI Ideas changes
      </div>
      <div className="space-y-1">
        {newPicks.slice(0, 3).map((p) => (
          <div key={`n-${p.symbol}`} className="text-[13px] flex items-center gap-1.5">
            <span className="text-emerald-400 font-semibold">+ new</span>
            <span className="font-mono text-white">{p.symbol}</span>
            <span className="text-[var(--text-muted)] truncate">(score {p.score})</span>
          </div>
        ))}
        {dropped.slice(0, 3).map((p) => (
          <div key={`d-${p.symbol}`} className="text-[13px] flex items-center gap-1.5">
            <span className="text-red-400 font-semibold">− drop</span>
            <span className="font-mono text-white">{p.symbol}</span>
          </div>
        ))}
        {shifts.slice(0, 3).map((s) => {
          const up = s.delta > 0;
          return (
            <div key={`s-${s.symbol}`} className="text-[13px] flex items-center gap-1.5">
              <span className={up ? "text-emerald-400" : "text-red-400"}>{up ? "↑ shift" : "↓ shift"}</span>
              <span className="font-mono text-white">{s.symbol}</span>
              <span className="text-[var(--text-muted)]">{s.old} → {s.new}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function MorningSummaryCard() {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [sending, setSending] = useState(false);

  const load = useCallback(async (force = false) => {
    if (force) setRegenerating(true);
    else setLoading(true);
    try {
      const { data } = await getMorningSummary(force);
      setDoc(data);
    } catch {
      setDoc(null);
    } finally {
      setLoading(false);
      setRegenerating(false);
    }
  }, []);

  useEffect(() => { load(false); }, [load]);

  const handleSend = async () => {
    setSending(true);
    try {
      const { data } = await sendMorningToTelegram();
      if (data.ok) {
        toast.success(`Morning brief sent to ${data.sent_to?.length || 0} Telegram chat(s).`);
        await load(false);
      } else {
        toast.error(data.error || "Send failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-amber-500/5 to-pink-500/5 border border-amber-500/20 rounded-lg p-4 animate-pulse">
        <div className="h-4 w-40 bg-white/10 rounded mb-3" />
        <div className="h-20 bg-white/5 rounded" />
      </div>
    );
  }
  if (!doc || !doc.summary) return null;

  const sentToTg = doc.sent_to_telegram_at;
  const tone = doc.market_sentiment?.label || "neutral";
  const toneEmoji = { positive: "🟢", negative: "🔴", neutral: "⚪" }[tone] || "⚪";

  return (
    <div
      className="bg-gradient-to-br from-amber-500/[0.04] to-pink-500/[0.04] border border-amber-500/20 rounded-lg p-4"
      data-testid="morning-summary-card"
    >
      <div className="flex items-start justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Sunrise size={16} className="text-amber-400" />
          <div>
            <h3 className="text-sm font-semibold text-white" style={{ fontFamily: "Chivo" }}>
              Morning Brief
            </h3>
            <div className="text-[10px] text-[var(--text-muted)] mt-0.5">
              {toneEmoji} {tone} tone · {doc.date} · AI-generated
            </div>
          </div>
        </div>
        <div className="flex gap-1">
          <button
            onClick={() => load(true)}
            disabled={regenerating}
            className="p-1.5 text-[var(--text-muted)] hover:text-amber-300 rounded hover:bg-amber-500/10 transition-colors"
            title="Regenerate"
            data-testid="regen-morning-btn"
          >
            <RefreshCw size={12} className={regenerating ? "animate-spin" : ""} />
          </button>
          <button
            onClick={handleSend}
            disabled={sending}
            className="p-1.5 text-amber-300 hover:text-amber-100 rounded hover:bg-amber-500/15 transition-colors"
            title="Send to Telegram"
            data-testid="send-morning-tg-btn"
          >
            <Send size={12} className={sending ? "animate-pulse" : ""} />
          </button>
        </div>
      </div>
      <div className="text-sm text-[var(--text-secondary)] leading-relaxed whitespace-pre-wrap max-h-[320px] overflow-y-auto" data-testid="morning-summary-text">
        {doc.summary}
      </div>
      <BrokerStrip rows={doc.broker_breakdown} label="since close" />
      <IdeaChangesStrip changes={doc.idea_changes} />
      {sentToTg && (
        <div className="text-[10px] text-emerald-400/80 mt-2 flex items-center gap-1 border-t border-white/5 pt-2">
          <Send size={10} /> Sent to Telegram at {new Date(sentToTg).toLocaleTimeString()}
        </div>
      )}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-3 py-1 rounded text-[11px] transition-colors ${
        active
          ? "bg-[var(--brand)]/15 text-[var(--brand)] border border-[var(--brand)]/30"
          : "text-[var(--text-muted)] hover:text-white border border-transparent"
      }`}
    >
      {children}
    </button>
  );
}

export function EveningWrapCard() {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [sending, setSending] = useState(false);

  const load = useCallback(async (force = false) => {
    if (force) setRegenerating(true);
    else setLoading(true);
    try {
      const { data } = await getEveningWrap(force);
      setDoc(data);
    } catch {
      setDoc(null);
    } finally {
      setLoading(false);
      setRegenerating(false);
    }
  }, []);

  useEffect(() => { load(false); }, [load]);

  const handleSend = async () => {
    setSending(true);
    try {
      const { data } = await sendEveningToTelegram();
      if (data.ok) {
        toast.success(`Evening wrap sent to ${data.sent_to?.length || 0} Telegram chat(s).`);
        await load(false);
      } else {
        toast.error(data.error || "Send failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-indigo-500/5 to-purple-500/5 border border-indigo-500/20 rounded-lg p-4 animate-pulse">
        <div className="h-4 w-40 bg-white/10 rounded mb-3" />
        <div className="h-20 bg-white/5 rounded" />
      </div>
    );
  }
  if (!doc || !doc.summary) return null;

  const pm = doc.portfolio_move || {};
  const dp = pm.delta_pct;
  const dv = pm.delta_value;
  const up = (dp || 0) >= 0;
  const moveColor = up ? "text-emerald-400" : "text-red-400";
  const sentToTg = doc.sent_to_telegram_at;

  return (
    <div
      className="bg-gradient-to-br from-indigo-500/[0.04] to-purple-500/[0.04] border border-indigo-500/20 rounded-lg p-4"
      data-testid="evening-wrap-card"
    >
      <div className="flex items-start justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Sunset size={16} className="text-indigo-300" />
          <div>
            <h3 className="text-sm font-semibold text-white" style={{ fontFamily: "Chivo" }}>
              Evening Wrap
            </h3>
            <div className="text-[10px] text-[var(--text-muted)] mt-0.5 flex items-center gap-1 flex-wrap">
              {pm.today_value ? (
                <span className="font-mono">
                  ₹{(pm.today_value/100000).toFixed(1)}L
                  {dp !== null && dp !== undefined && !pm.insufficient_data && (
                    <span className={`ml-1 ${moveColor}`}>
                      ({up ? "+" : ""}{dv?.toLocaleString("en-IN", {maximumFractionDigits: 0})} / {up ? "+" : ""}{dp.toFixed(2)}%)
                    </span>
                  )}
                </span>
              ) : "—"}
              <span>· {doc.date}</span>
              <span>· AI-generated</span>
            </div>
          </div>
        </div>
        <div className="flex gap-1">
          <button
            onClick={() => load(true)}
            disabled={regenerating}
            className="p-1.5 text-[var(--text-muted)] hover:text-indigo-300 rounded hover:bg-indigo-500/10 transition-colors"
            title="Regenerate"
            data-testid="regen-evening-btn"
          >
            <RefreshCw size={12} className={regenerating ? "animate-spin" : ""} />
          </button>
          <button
            onClick={handleSend}
            disabled={sending}
            className="p-1.5 text-indigo-300 hover:text-indigo-100 rounded hover:bg-indigo-500/15 transition-colors"
            title="Send to Telegram"
            data-testid="send-evening-tg-btn"
          >
            <Send size={12} className={sending ? "animate-pulse" : ""} />
          </button>
        </div>
      </div>

      <div className="text-sm text-[var(--text-secondary)] leading-relaxed whitespace-pre-wrap max-h-[320px] overflow-y-auto" data-testid="evening-wrap-text">
        {doc.summary}
      </div>

      {/* Best / Worst strip */}
      {(pm.best?.length > 0 || pm.worst?.length > 0) && (
        <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-white/5">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[var(--text-muted)] mb-2 flex items-center gap-1.5">
              <TrendingUp size={12} className="text-emerald-400" /> Top gainers
            </div>
            <div className="space-y-1">
              {(pm.best || []).slice(0, 3).map((b) => (
                <div key={b.symbol} className="text-[13px] flex justify-between">
                  <span className="font-mono text-white">{b.symbol}</span>
                  <span className="font-mono text-emerald-400">+{b.delta_pct.toFixed(2)}%</span>
                </div>
              ))}
              {(!pm.best || pm.best.length === 0) && <div className="text-[13px] text-[var(--text-muted)]">—</div>}
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[var(--text-muted)] mb-2 flex items-center gap-1.5">
              <TrendingDown size={12} className="text-red-400" /> Top losers
            </div>
            <div className="space-y-1">
              {(pm.worst || []).slice(0, 3).map((w) => (
                <div key={w.symbol} className="text-[13px] flex justify-between">
                  <span className="font-mono text-white">{w.symbol}</span>
                  <span className="font-mono text-red-400">{w.delta_pct.toFixed(2)}%</span>
                </div>
              ))}
              {(!pm.worst || pm.worst.length === 0) && <div className="text-[13px] text-[var(--text-muted)]">—</div>}
            </div>
          </div>
        </div>
      )}

      {/* Alerts indicator */}
      {doc.alerts_fired?.length > 0 && (
        <div className="flex items-center gap-1.5 mt-3 pt-2 border-t border-white/5 text-[10px] text-[var(--text-muted)]">
          <Bell size={10} className="text-amber-400" />
          {doc.alerts_fired.length} alert{doc.alerts_fired.length !== 1 ? "s" : ""} fired in last 24h
        </div>
      )}

      <BrokerStrip rows={doc.broker_breakdown} label="today" />
      <IdeaChangesStrip changes={doc.idea_changes} />

      {sentToTg && (
        <div className="text-[10px] text-emerald-400/80 mt-2 flex items-center gap-1 border-t border-white/5 pt-2">
          <Send size={10} /> Sent to Telegram at {new Date(sentToTg).toLocaleTimeString()}
        </div>
      )}
    </div>
  );
}


export function WeekendReviewCard() {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [emailing, setEmailing] = useState(false);

  const load = useCallback(async (force = false) => {
    if (force) setRegenerating(true);
    else setLoading(true);
    try {
      const { data } = await getWeekendReview(force);
      setDoc(data);
    } catch {
      setDoc(null);
    } finally {
      setLoading(false);
      setRegenerating(false);
    }
  }, []);

  useEffect(() => { load(false); }, [load]);

  const handleSend = async () => {
    setSending(true);
    try {
      const { data } = await sendWeekendToTelegram();
      if (data.ok) {
        toast.success(`Weekend review sent to ${data.sent_to?.length || 0} Telegram chat(s).`);
        await load(false);
      } else {
        toast.error(data.error || "Send failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const handleEmail = async () => {
    setEmailing(true);
    try {
      const { data } = await sendWeekendToEmail();
      if (data.ok) {
        toast.success(`Weekend review emailed to ${data.to}.`);
        await load(false);
      } else {
        toast.error(data.error || "Email send failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Email send failed");
    } finally {
      setEmailing(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-teal-500/5 to-cyan-500/5 border border-teal-500/20 rounded-lg p-4 animate-pulse">
        <div className="h-4 w-40 bg-white/10 rounded mb-3" />
        <div className="h-20 bg-white/5 rounded" />
      </div>
    );
  }
  if (!doc || !doc.summary) return null;

  const pm = doc.portfolio_move_week || {};
  const dp = pm.delta_pct;
  const dv = pm.delta_value;
  const up = (dp || 0) >= 0;
  const moveColor = up ? "text-emerald-400" : "text-red-400";
  const sentToTg = doc.sent_to_telegram_at;
  const sentToEmail = doc.sent_to_email_at;
  const best = doc.best_week || [];
  const worst = doc.worst_week || [];
  const alertsGrouped = doc.alerts_grouped || {};
  const alertsTotal = doc.alerts_count || 0;

  return (
    <div
      className="bg-gradient-to-br from-teal-500/[0.04] to-cyan-500/[0.04] border border-teal-500/20 rounded-lg p-4"
      data-testid="weekend-review-card"
    >
      <div className="flex items-start justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <CalendarDays size={16} className="text-teal-300" />
          <div>
            <h3 className="text-sm font-semibold text-white" style={{ fontFamily: "Chivo" }}>
              Weekend Review
            </h3>
            <div className="text-[10px] text-[var(--text-muted)] mt-0.5 flex items-center gap-1 flex-wrap">
              <span>Week of {doc.saturday_date}</span>
              {pm.today_value ? (
                <span className="font-mono">
                  · ₹{(pm.today_value / 100000).toFixed(1)}L
                  {dp !== null && dp !== undefined && !pm.insufficient_data && (
                    <span className={`ml-1 ${moveColor}`}>
                      ({up ? "+" : ""}{dv?.toLocaleString("en-IN", { maximumFractionDigits: 0 })} / {up ? "+" : ""}{dp.toFixed(2)}%)
                    </span>
                  )}
                </span>
              ) : null}
              <span>· AI-generated</span>
            </div>
          </div>
        </div>
        <div className="flex gap-1">
          <button
            onClick={() => load(true)}
            disabled={regenerating}
            className="p-1.5 text-[var(--text-muted)] hover:text-teal-300 rounded hover:bg-teal-500/10 transition-colors"
            title="Regenerate"
            data-testid="regen-weekend-btn"
          >
            <RefreshCw size={12} className={regenerating ? "animate-spin" : ""} />
          </button>
          <button
            onClick={handleSend}
            disabled={sending}
            className="p-1.5 text-teal-300 hover:text-teal-100 rounded hover:bg-teal-500/15 transition-colors"
            title="Send to Telegram"
            data-testid="send-weekend-tg-btn"
          >
            <Send size={12} className={sending ? "animate-pulse" : ""} />
          </button>
          <button
            onClick={handleEmail}
            disabled={emailing}
            className="p-1.5 text-cyan-300 hover:text-cyan-100 rounded hover:bg-cyan-500/15 transition-colors"
            title="Send to Email"
            data-testid="send-weekend-email-btn"
          >
            <Mail size={12} className={emailing ? "animate-pulse" : ""} />
          </button>
        </div>
      </div>

      <div className="text-sm text-[var(--text-secondary)] leading-relaxed whitespace-pre-wrap max-h-[380px] overflow-y-auto" data-testid="weekend-review-text">
        {doc.summary}
      </div>

      {(best.length > 0 || worst.length > 0) && (
        <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-white/5">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[var(--text-muted)] mb-2 flex items-center gap-1.5">
              <TrendingUp size={12} className="text-emerald-400" /> Best (wk)
            </div>
            <div className="space-y-1">
              {best.slice(0, 5).map((b) => (
                <div key={b.symbol} className="text-[13px] flex justify-between">
                  <span className="font-mono text-white">{b.symbol}</span>
                  <span className="font-mono text-emerald-400">+{b.delta_pct.toFixed(2)}%</span>
                </div>
              ))}
              {best.length === 0 && <div className="text-[13px] text-[var(--text-muted)]">—</div>}
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[var(--text-muted)] mb-2 flex items-center gap-1.5">
              <TrendingDown size={12} className="text-red-400" /> Worst (wk)
            </div>
            <div className="space-y-1">
              {worst.slice(0, 5).map((w) => (
                <div key={w.symbol} className="text-[13px] flex justify-between">
                  <span className="font-mono text-white">{w.symbol}</span>
                  <span className="font-mono text-red-400">{w.delta_pct.toFixed(2)}%</span>
                </div>
              ))}
              {worst.length === 0 && <div className="text-[13px] text-[var(--text-muted)]">—</div>}
            </div>
          </div>
        </div>
      )}

      <BrokerStrip rows={doc.broker_breakdown_week} label="this week" />
      <IdeaChangesStrip changes={doc.idea_changes} />

      {alertsTotal > 0 && (
        <div className="flex items-center gap-1.5 mt-3 pt-2 border-t border-white/5 text-[10px] text-[var(--text-muted)] flex-wrap">
          <Bell size={10} className="text-amber-400" />
          <span>{alertsTotal} alerts this week:</span>
          {Object.entries(alertsGrouped).map(([t, n]) => (
            <span key={t} className="px-1.5 py-0.5 rounded bg-white/5 border border-white/10 font-mono">
              {t}={n}
            </span>
          ))}
        </div>
      )}

      {sentToTg && (
        <div className="text-[10px] text-emerald-400/80 mt-2 flex items-center gap-1 border-t border-white/5 pt-2">
          <Send size={10} /> Sent to Telegram at {new Date(sentToTg).toLocaleString()}
        </div>
      )}
      {sentToEmail && (
        <div className="text-[10px] text-cyan-400/80 mt-1 flex items-center gap-1">
          <Mail size={10} /> Emailed at {new Date(sentToEmail).toLocaleString()}
        </div>
      )}
    </div>
  );
}

// ─── Closing Bell — 15:35 IST top movers digest ───────────────────────────
export function ClosingBellCard() {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sending, setSending] = useState(false);

  const load = useCallback(async (force = false) => {
    if (force) setRefreshing(true);
    else setLoading(true);
    try {
      const { data } = await getClosingBell(force);
      if (data) setDoc(data);
    } catch (err) {
      // On refresh failure, keep existing doc visible and show a toast instead of hiding the card.
      // Only null the doc on *initial* load failure (where there's nothing to preserve anyway).
      if (force) {
        toast.error(err?.response?.data?.detail || "Couldn't refresh — showing previous data.");
      } else {
        setDoc(null);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(false); }, [load]);

  const handleSend = async () => {
    setSending(true);
    try {
      const { data } = await sendClosingBellToTelegram();
      if (data.ok) {
        toast.success(`Closing bell sent to ${data.sent_to?.length || 0} Telegram chat(s).`);
        await load(false);
      } else {
        toast.error(data.error || "Send failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-amber-500/5 to-orange-500/5 border border-amber-500/20 rounded-lg p-4 animate-pulse" data-testid="closing-bell-loading">
        <div className="h-4 w-40 bg-white/10 rounded mb-3" />
        <div className="h-20 bg-white/5 rounded" />
      </div>
    );
  }
  if (!doc) return null;

  const gainers = doc.gainers || [];
  const losers = doc.losers || [];
  const universe = doc.total_universe || 0;
  const sentToTg = doc.sent_to_telegram_at;
  const isEmpty = gainers.length === 0 && losers.length === 0;

  const fmtMoney = (v, c) => {
    if (v == null) return "—";
    const sign = v >= 0 ? "+" : "−";
    const a = Math.abs(v);
    if (c === "US") return `${sign}$${a.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
    return `${sign}₹${a.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
  };

  return (
    <div
      className="bg-gradient-to-br from-amber-500/[0.04] to-orange-500/[0.04] border border-amber-500/20 rounded-lg p-4"
      data-testid="closing-bell-card"
    >
      <div className="flex items-start justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <BellRing size={16} className="text-amber-300" />
          <div>
            <h3 className="text-sm font-semibold text-white" style={{ fontFamily: "Chivo" }}>
              Closing Bell · Today's Movers
            </h3>
            <div className="text-[10px] text-[var(--text-muted)] mt-0.5 flex items-center gap-1 flex-wrap">
              <span>{doc.date}</span>
              <span>· {universe} of your equities</span>
              <span>· auto-sent to Telegram at 3:35 PM IST</span>
            </div>
          </div>
        </div>
        <div className="flex gap-1">
          <button
            onClick={() => load(true)}
            disabled={refreshing}
            className="p-1.5 text-[var(--text-muted)] hover:text-amber-300 rounded hover:bg-amber-500/10 transition-colors"
            title="Refresh"
            data-testid="refresh-closing-bell-btn"
          >
            <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} />
          </button>
          <button
            onClick={handleSend}
            disabled={sending || isEmpty}
            className="p-1.5 text-amber-300 hover:text-amber-100 rounded hover:bg-amber-500/15 transition-colors disabled:opacity-40"
            title={isEmpty ? "Nothing to send (markets closed/holiday)" : "Send to Telegram now"}
            data-testid="send-closing-bell-tg-btn"
          >
            <Send size={12} className={sending ? "animate-pulse" : ""} />
          </button>
        </div>
      </div>

      {isEmpty ? (
        <div className="text-xs text-[var(--text-muted)] py-3 text-center">
          Markets quiet — no notable moves in your portfolio yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="closing-bell-grid">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[var(--text-muted)] mb-2 flex items-center gap-1.5">
              <TrendingUp size={12} className="text-emerald-400" /> Top gainers
            </div>
            <div className="space-y-1.5">
              {gainers.length === 0 && (
                <div className="text-[11px] text-[var(--text-muted)]">No gainers in your portfolio today.</div>
              )}
              {gainers.map(r => (
                <div key={`g-${r.symbol}-${r.country}`} className="space-y-0.5" data-testid={`closing-bell-gainer-${r.symbol}`}>
                  <div className="flex items-center justify-between text-[12px]">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-mono font-semibold text-white truncate">{r.symbol}</span>
                      <span className="text-[9px] text-[var(--text-muted)] uppercase">{r.country}</span>
                      {r.sector && <span className="text-[10px] text-[var(--text-muted)] truncate">· {r.sector}</span>}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-emerald-400/70 font-mono text-[10px]">{fmtMoney(r.day_change_total, r.country)}</span>
                      <span className="text-emerald-400 font-mono font-semibold">+{(r.day_change_pct || 0).toFixed(2)}%</span>
                    </div>
                  </div>
                  {r.reason && (
                    <div className="text-[11px] text-[var(--text-muted)] italic pl-3 border-l border-emerald-500/20 ml-1" data-testid={`closing-bell-reason-${r.symbol}`}>
                      ↳ {r.reason}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[var(--text-muted)] mb-2 flex items-center gap-1.5">
              <TrendingDown size={12} className="text-red-400" /> Top losers
            </div>
            <div className="space-y-1.5">
              {losers.length === 0 && (
                <div className="text-[11px] text-[var(--text-muted)]">No losers in your portfolio today.</div>
              )}
              {losers.map(r => (
                <div key={`l-${r.symbol}-${r.country}`} className="space-y-0.5" data-testid={`closing-bell-loser-${r.symbol}`}>
                  <div className="flex items-center justify-between text-[12px]">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-mono font-semibold text-white truncate">{r.symbol}</span>
                      <span className="text-[9px] text-[var(--text-muted)] uppercase">{r.country}</span>
                      {r.sector && <span className="text-[10px] text-[var(--text-muted)] truncate">· {r.sector}</span>}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-red-400/70 font-mono text-[10px]">{fmtMoney(r.day_change_total, r.country)}</span>
                      <span className="text-red-400 font-mono font-semibold">{(r.day_change_pct || 0).toFixed(2)}%</span>
                    </div>
                  </div>
                  {r.reason && (
                    <div className="text-[11px] text-[var(--text-muted)] italic pl-3 border-l border-red-500/20 ml-1" data-testid={`closing-bell-reason-${r.symbol}`}>
                      ↳ {r.reason}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {sentToTg && (
        <div className="text-[10px] text-amber-400/80 mt-3 flex items-center gap-1">
          <Send size={10} /> Sent to Telegram at {new Date(sentToTg).toLocaleString()}
        </div>
      )}
    </div>
  );
}
